/// React hook for AI chat lifecycle: send message with streaming, abort,
/// load/save history via IndexedDB, and element explanation.
///
/// Integrates aiStore for state management, editorStore for diagram context,
/// and the OpenRouter streaming client for LLM communication.

import { useCallback, useEffect, useRef } from "react";
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import { useAIStore, ensureAPIKey } from "@/stores/aiStore";
import { useEditorStore } from "@/stores/editorStore";
import { streamChatCompletion } from "@/services/openrouter/client";
import {
  CHAT_SYSTEM_PROMPT,
  ELEMENT_EXPLANATION_PROMPT,
  BPMN_GENERATION_SYSTEM_PROMPT,
  N8N_GENERATION_SYSTEM_PROMPT,
  buildDiagramContext,
} from "@/services/openrouter/prompts";
import { OpenRouterError } from "@/types/ai";
import type { ChatMessage, DiagnosisRequest } from "@/types/ai";
import { extractDiagramContent } from "@/components/ai/AIGeneratedDiagram";

// ---------------------------------------------------------------------------
// IndexedDB key helpers
// ---------------------------------------------------------------------------

/** Derive the IndexedDB key for a given editor tab's chat history. */
function chatKey(tabId: string): string {
  return `flowy-chat-${tabId}`;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

interface UseAIChatReturn {
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>;
  abortResponse: () => void;
  loadHistory: (tabId: string) => Promise<void>;
  clearHistory: (tabId: string) => Promise<void>;
  explainElement: () => Promise<void>;
  generateBPMN: (description: string) => Promise<void>;
  generateNodeFlow: (description: string) => Promise<void>;
  refineDiagram: (followUp: string) => Promise<void>;
  diagnoseError: (request: DiagnosisRequest) => Promise<void>;
  isStreaming: boolean;
}

interface SendMessageOptions {
  systemPrompt?: string;
  includeContext?: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAIChat(): UseAIChatReturn {
  const isStreaming = useAIStore((s) => s.isStreaming);
  const lastTabIdRef = useRef<string | null>(null);

  // -------------------------------------------------------------------------
  // Load history from IndexedDB
  // -------------------------------------------------------------------------

  const loadHistory = useCallback(async (tabId: string) => {
    try {
      const saved = await idbGet<ChatMessage[]>(chatKey(tabId));
      if (saved && Array.isArray(saved) && saved.length > 0) {
        const store = useAIStore.getState();
        // Ensure conversation exists, then replace messages
        store.getConversation(tabId);
        const conversations = new Map(useAIStore.getState().conversations);
        const conversation = conversations.get(tabId);
        if (conversation) {
          conversations.set(tabId, {
            ...conversation,
            messages: saved,
            updatedAt: Date.now(),
          });
          useAIStore.setState({ conversations });
        }
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Save history to IndexedDB
  // -------------------------------------------------------------------------

  const saveHistory = useCallback(async (tabId: string) => {
    try {
      const conversation = useAIStore.getState().conversations.get(tabId);
      if (conversation && conversation.messages.length > 0) {
        await idbSet(chatKey(tabId), conversation.messages);
      }
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Clear history
  // -------------------------------------------------------------------------

  const clearHistory = useCallback(async (tabId: string) => {
    try {
      await idbDel(chatKey(tabId));
      useAIStore.getState().clearConversation(tabId);
    } catch (error) {
      console.error("Failed to clear chat history:", error);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Send message with streaming
  // -------------------------------------------------------------------------

  const sendMessage = useCallback(
    async (content: string, options?: SendMessageOptions) => {
      // Check API key
      if (!ensureAPIKey()) return;

      const aiState = useAIStore.getState();
      const editorState = useEditorStore.getState();
      const activeTab = editorState.getActiveTab();
      const tabId = activeTab?.id ?? "default";
      const systemPrompt = options?.systemPrompt ?? CHAT_SYSTEM_PROMPT;
      const includeContext = options?.includeContext ?? true;

      // 1. Add user message to conversation
      const userMessage: ChatMessage = {
        role: "user",
        content,
        timestamp: Date.now(),
      };
      aiState.addMessage(tabId, userMessage);

      // 2. Build messages array for LLM
      const conversation = useAIStore.getState().getConversation(tabId);
      const contextWindowSize = aiState.contextWindowSize;
      const recentMessages = conversation.messages.slice(-contextWindowSize);

      const llmMessages: ChatMessage[] = [
        { role: "system", content: systemPrompt, timestamp: Date.now() },
      ];

      // Prepend diagram context if requested and a tab is active
      if (includeContext && activeTab) {
        const editorTabState = editorState.getActiveEditorState();
        const diagramContext = buildDiagramContext(activeTab, editorTabState?.selection);
        llmMessages.push({
          role: "system",
          content: `Current diagram context:\n${diagramContext}`,
          timestamp: Date.now(),
        });
      }

      llmMessages.push(...recentMessages);

      // 3. Create empty assistant message for streaming accumulation
      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: "",
        timestamp: Date.now(),
        model: aiState.selectedModel,
      };
      aiState.addMessage(tabId, assistantMessage);

      // 4. Set streaming state (creates AbortController)
      aiState.setStreaming(tabId);
      const abortController = useAIStore.getState().abortController;

      // 5. Stream response
      try {
        let accumulated = "";
        const stream = streamChatCompletion(
          aiState.apiKey ?? "",
          aiState.selectedModel,
          llmMessages,
          abortController?.signal,
        );

        for await (const delta of stream) {
          accumulated += delta;
          useAIStore.getState().updateLastAssistantMessage(tabId, accumulated);
        }

        // 6. Streaming complete
        useAIStore.getState().setStreaming(null);
        await saveHistory(tabId);
      } catch (error) {
        useAIStore.getState().setStreaming(null);

        if (error instanceof DOMException && error.name === "AbortError") {
          // Abort: keep partial content, save what we have
          await saveHistory(tabId);
          return;
        }

        // Build error message with full details from OpenRouter
        let errorContent = "An error occurred while generating a response.";
        if (error instanceof OpenRouterError) {
          console.error("OpenRouter error details:", error.rawBody);
          const rawMsg =
            typeof error.rawBody === "object" && error.rawBody !== null
              ? (((error.rawBody as Record<string, unknown>).error as Record<string, unknown>)
                  ?.message as string) ?? ""
              : "";
          const detail =
            error.status === 401
              ? "Please check your API key in settings."
              : error.status === 429
                ? "Rate limit exceeded. Please wait and try again."
                : rawMsg || "Please try again.";
          errorContent = `OpenRouter API Error (${error.status}): ${detail}`;
        } else if (error instanceof Error) {
          errorContent = `Error: ${error.message}`;
        }

        // Add error as a special assistant message
        const errorMsg: ChatMessage = {
          role: "assistant",
          content: errorContent,
          timestamp: Date.now(),
          model: aiState.selectedModel,
          isError: true,
        };
        useAIStore.getState().addMessage(tabId, errorMsg);
        await saveHistory(tabId);
      }
    },
    [saveHistory],
  );

  // -------------------------------------------------------------------------
  // Abort response
  // -------------------------------------------------------------------------

  const abortResponse = useCallback(() => {
    useAIStore.getState().abortStreaming();
  }, []);

  // -------------------------------------------------------------------------
  // Explain selected element
  // -------------------------------------------------------------------------

  const explainElement = useCallback(async () => {
    const editorState = useEditorStore.getState();
    const activeEditorState = editorState.getActiveEditorState();
    const selection = activeEditorState?.selection;

    if (!selection || selection.selectedIds.length === 0) {
      // No selection: add a hint message
      const tabId = editorState.activeTabId ?? "default";
      const hintMsg: ChatMessage = {
        role: "assistant",
        content: "Select an element first, then ask me to explain it.",
        timestamp: Date.now(),
      };
      useAIStore.getState().addMessage(tabId, hintMsg);
      return;
    }

    await sendMessage("What does this element do?", {
      systemPrompt: ELEMENT_EXPLANATION_PROMPT,
      includeContext: true,
    });
  }, [sendMessage]);

  // -------------------------------------------------------------------------
  // Generate BPMN diagram from natural language
  // -------------------------------------------------------------------------

  const generateBPMN = useCallback(
    async (description: string) => {
      await sendMessage(description, {
        systemPrompt: BPMN_GENERATION_SYSTEM_PROMPT,
        includeContext: false,
      });

      // After streaming completes, check for generated content
      const aiState = useAIStore.getState();
      const editorState = useEditorStore.getState();
      const tabId = editorState.activeTabId ?? "default";
      const conversation = aiState.conversations.get(tabId);
      if (!conversation) return;

      const lastMsg = conversation.messages[conversation.messages.length - 1];
      if (lastMsg?.role === "assistant" && !lastMsg.isError) {
        const extracted = extractDiagramContent(lastMsg.content, "bpmn");
        if (extracted) {
          const genTabId = `ai-bpmn-${Date.now()}`;

          // Track generation result for iterative refinement
          aiState.setGenerationResult(genTabId, {
            type: "bpmn",
            content: extracted,
            rawResponse: lastMsg.content,
          });

          // Automatically open the generated BPMN in a new editor tab.
          // The EditorCanvas will auto-load the content when its WASM worker becomes ready.
          useEditorStore.getState().addTab({
            id: genTabId,
            title: "[AI] BPMN Process",
            editorType: "bpmn",
            content: extracted,
            dirty: false,
            canUndo: false,
            canRedo: false,
          });
        }
      }
    },
    [sendMessage],
  );

  // -------------------------------------------------------------------------
  // Generate node flow from natural language
  // -------------------------------------------------------------------------

  const generateNodeFlow = useCallback(
    async (description: string) => {
      await sendMessage(description, {
        systemPrompt: N8N_GENERATION_SYSTEM_PROMPT,
        includeContext: false,
      });

      // After streaming completes, check for generated content
      const aiState = useAIStore.getState();
      const editorState = useEditorStore.getState();
      const tabId = editorState.activeTabId ?? "default";
      const conversation = aiState.conversations.get(tabId);
      if (!conversation) return;

      const lastMsg = conversation.messages[conversation.messages.length - 1];
      if (lastMsg?.role === "assistant" && !lastMsg.isError) {
        const extracted = extractDiagramContent(lastMsg.content, "nodeflow");
        if (extracted) {
          const genTabId = `ai-nodeflow-${Date.now()}`;

          // Track generation result for iterative refinement
          aiState.setGenerationResult(genTabId, {
            type: "nodeflow",
            content: extracted,
            rawResponse: lastMsg.content,
          });

          // Automatically open the generated node flow in a new editor tab.
          // The EditorCanvas will auto-load the content when its WASM worker becomes ready.
          useEditorStore.getState().addTab({
            id: genTabId,
            title: "[AI] Node Flow",
            editorType: "nodeflow",
            content: extracted,
            dirty: false,
            canUndo: false,
            canRedo: false,
          });
        }
      }
    },
    [sendMessage],
  );

  // -------------------------------------------------------------------------
  // Refine a previously generated diagram via follow-up
  // -------------------------------------------------------------------------

  const refineDiagram = useCallback(
    async (followUp: string) => {
      const aiState = useAIStore.getState();
      const lastTabId = aiState.lastGenerationTabId;
      if (!lastTabId) {
        // No previous generation to refine, fall back to regular send
        await sendMessage(followUp);
        return;
      }

      const lastResult = aiState.getGenerationResult(lastTabId);
      if (!lastResult) {
        await sendMessage(followUp);
        return;
      }

      // Use the same system prompt as the original generation
      const systemPrompt =
        lastResult.type === "bpmn" ? BPMN_GENERATION_SYSTEM_PROMPT : N8N_GENERATION_SYSTEM_PROMPT;

      await sendMessage(followUp, {
        systemPrompt,
        includeContext: true,
      });

      // After streaming, update the existing tab with new content
      const editorState = useEditorStore.getState();
      const currentTabId = editorState.activeTabId ?? "default";
      const conversation = useAIStore.getState().conversations.get(currentTabId);
      if (!conversation) return;

      const lastMsg = conversation.messages[conversation.messages.length - 1];
      if (lastMsg?.role === "assistant" && !lastMsg.isError) {
        const extracted = extractDiagramContent(lastMsg.content, lastResult.type);
        if (extracted) {
          // Update the generation result
          useAIStore.getState().setGenerationResult(lastTabId, {
            type: lastResult.type,
            content: extracted,
            rawResponse: lastMsg.content,
          });

          // Dispatch loadDocument to update the existing tab
          window.dispatchEvent(
            new CustomEvent("flowy:loadDocument", {
              detail: {
                content: extracted,
                format: lastResult.type,
                tabId: lastTabId,
              },
            }),
          );
        }
      }
    },
    [sendMessage],
  );

  // -------------------------------------------------------------------------
  // Diagnose error (wrapper for aiStore.triggerDiagnosis)
  // -------------------------------------------------------------------------

  const diagnoseError = useCallback(async (request: DiagnosisRequest) => {
    await useAIStore.getState().triggerDiagnosis(request);
  }, []);

  // -------------------------------------------------------------------------
  // Auto-load history when active tab changes
  // -------------------------------------------------------------------------

  const activeTabId = useEditorStore((s) => s.activeTabId);

  useEffect(() => {
    if (activeTabId && activeTabId !== lastTabIdRef.current) {
      lastTabIdRef.current = activeTabId;
      loadHistory(activeTabId);
    }
  }, [activeTabId, loadHistory]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    sendMessage,
    abortResponse,
    loadHistory,
    clearHistory,
    explainElement,
    generateBPMN,
    generateNodeFlow,
    refineDiagram,
    diagnoseError,
    isStreaming,
  };
}
